/* eslint-disable no-loop-func */
import eventtypecommon from './eventtypecommon';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { EventType } from '../../types/ViewModel/EventType';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import EventTypeFactory from '../../factory/EventTypeFactory';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import eventtypehandler from './eventtypehandler';
import { EventTypeProcessOverview } from '../../types/ViewModel/EventTypeProcessOverview';
import { EventTypeProcess } from '../../types/ViewModel/EventTypeProcess';
import { EventTypeProcessInstance } from '../../types/ViewModel/EventTypeProcessInstance';
import { ProcessItemList } from '../../types/ViewModel/ProcessItemList';
import { ProcessItem } from '../../types/ViewModel/ProcessItem';
import useRotation from '../../app/useRotation';
import useEventType from '../../app/useEventType';
import useEvent from '../../app/useEvent';
import { ToolboxQueryInfo } from '../../types/ViewModel/ToolboxQueryInfo';
import eventTypeGroupChangeLogUtils from './eventTypeGroupChangeLogUtils';

export default () => {
    const { getAvailableEventTypes, getMandatoryOptionalList, filterOnContext } = eventtypehandler();
    const { getRequiredContextObj } = useRotation();
    const { getEventType } = useEventType();
    const { filterSameEventTypeGroupAndLevel } = useEvent();
    const { findMissingEventTypeInSequence, hasConcurrentParentInCompleteTree, sortOrderedEventTypes } = eventtypecommon();
    const { createNewEventType } = EventTypeFactory();
    const { isMandatory, isMultiple, isConcurrent, getRequiredPreviousTypeId, getNotAfterEventTypeId } = eventTypeGroupChangeLogUtils();

    const setDisplayNameAndSortProcessList = (prcList: EventTypeProcess[]): EventTypeProcess[] => {
        prcList.forEach((prc) => {
            if (prc.isConcurrent || !prc.isSelectable) {
                prc.displayName = prc.eventType?.eventTypeGroup?.name;
            } else {
                prc.displayName = prc.eventType?.name;
            }
        });
        return prcList.sort((a, b) => a.displayName.localeCompare(b.displayName));
    };

    const getParentEventTypeListOfList = (
        eventArray: TankersEvent[],
        evType: EventType,
        evTypeArray: EventType[],
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): ProcessItemList[] => {
        const parentListList: ProcessItemList[] = [];

        if (evType.parentSequenceNumber) {
            // Mandatory event type with concurrent parent
            parentListList.push(new ProcessItemList(getParentEventTypeList(eventArray, evType, evType.parentSequenceNumber, evTypeArray, node)));
        } else {
            const reqPrevTypeId = getRequiredPreviousTypeId(evType.eventTypeGroup, node.firstTimeInPortCall);
            if (reqPrevTypeId) {
                const parentEvType: EventType | null = evTypeArray.find((et) => et.eventTypeId === reqPrevTypeId) ?? null;
                if (parentEvType) {
                    const eventListForParentGroup = eventArray.filter((ev) => ev.eventType?.eventTypeGroupId === parentEvType?.eventTypeGroupId);
                    if (eventListForParentGroup && eventListForParentGroup.length > 0) {
                        const concurrentParent = hasConcurrentParentInCompleteTree(evType, eventTypeData, node);
                        if (concurrentParent) {
                            // If parent is concurrent
                            let seqNrArray: number[] = eventListForParentGroup.flatMap((o) => o.concurrentSequenceNumber);
                            seqNrArray = seqNrArray.filter(function (v, i) {
                                return seqNrArray.indexOf(v) === i;
                            });
                            seqNrArray.forEach((seq) => {
                                // For each sequence, add a parent list if sequence is not finished
                                // const evArrayForSeq = eventArray.filter((ev) => ev.concurrentSequenceNumber === seq);
                                const evArrayForParentSeq = eventListForParentGroup.filter((ev) => ev.concurrentSequenceNumber === seq);

                                if (!getNotAfterEventTypeId(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                                    // For each sequence, add a parent list if:
                                    //  - concurrent parent sequence not finished
                                    //  - all sequences are finished
                                    //  - notAfterEventTypeId == null
                                    //  - requiredPreviousTypeId is dependent on last event type in group
                                    const concParentEvList = eventArray.filter(
                                        (ev) =>
                                            ev.eventType.eventTypeGroup.eventTypeGroupId === concurrentParent.eventTypeGroup.eventTypeGroupId &&
                                            ev.concurrentSequenceNumber === seq
                                    );
                                    let isMultipleOrNotFinished = true;
                                    if (!isMultiple(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                                        const evListForSeq = eventArray.filter(
                                            (ev) => ev.eventType.eventTypeGroupId === evType.eventTypeGroupId && ev.concurrentSequenceNumber === seq
                                        );
                                        if (evListForSeq && evListForSeq.length > 0) {
                                            const ind = findMissingEventTypeInSequence(evListForSeq);
                                            if (ind === -1) {
                                                isMultipleOrNotFinished = false;
                                            }
                                        }
                                    }

                                    const concParentSeqNotFinished = isSeqNotFinished(concParentEvList, concurrentParent, seq);
                                    if (
                                        isMultipleOrNotFinished &&
                                        concParentSeqNotFinished &&
                                        !isSeqNotFinished(evArrayForParentSeq, parentEvType, seq)
                                    ) {
                                        const sortedEvTypeList = sortOrderedEventTypes(parentEvType.eventTypeGroup?.eventTypeArray);
                                        if (
                                            sortedEvTypeList &&
                                            sortedEvTypeList.length > 0 &&
                                            sortedEvTypeList[sortedEvTypeList.length - 1].eventTypeId === evType.eventTypeGroup.requiredPreviousTypeId
                                        ) {
                                            parentListList.push(
                                                new ProcessItemList(getParentEventTypeList(evArrayForParentSeq, evType, seq, evTypeArray, node))
                                            );
                                        }
                                    }
                                } else if (isSeqNotFinished(evArrayForParentSeq, parentEvType, seq)) {
                                    parentListList.push(
                                        new ProcessItemList(getParentEventTypeList(evArrayForParentSeq, evType, seq, evTypeArray, node))
                                    );
                                }
                            });
                        } else {
                            // If parent is not concurrent
                            const ind = findMissingEventTypeInSequence(eventListForParentGroup);
                            if (ind !== -1 || !getNotAfterEventTypeId(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                                parentListList.push(new ProcessItemList(getParentEventTypeList(eventArray, evType, null, evTypeArray, node)));
                            }
                        }
                    }
                }
            }
        }
        return parentListList;
    };

    const getNextSeqNr = (eventArray: TankersEvent[], evType: EventType, seq: number | null, node: IRotationNode): number => {
        if (!seq) {
            const nextSeqnr = 1;
            if (isMultiple(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                const eventListForGroup = eventArray.filter((ev) => ev.eventType?.eventTypeGroupId === evType.eventTypeGroupId);
                if (eventListForGroup && eventListForGroup.length > 0) {
                    const eventListConcurrent = eventListForGroup.filter((o) => o.concurrentSequenceNumber);
                    if (eventListConcurrent && eventListConcurrent.length > 0) {
                        const maxSeqNr = Math.max(
                            ...eventListConcurrent.map(function (o) {
                                return o.concurrentSequenceNumber;
                            })
                        );
                        return maxSeqNr + 1;
                    }
                }
            }
            return nextSeqnr;
        }
        return seq;
    };

    const updateEventType = (eventArray: TankersEvent[], evType: EventType, seq: number | null, node: IRotationNode): EventType => {
        const reqPrevTypeId = getRequiredPreviousTypeId(evType.eventTypeGroup, node.firstTimeInPortCall);
        if (seq && reqPrevTypeId) {
            const parentEventType = getEventType(reqPrevTypeId, null);
            const evParentList = eventArray.filter(
                (o) => o.eventType?.eventTypeGroupId === parentEventType?.eventTypeGroupId && o.concurrentSequenceNumber === seq
            );
            const parentNotFinished = evTypeNotFinished(evParentList, parentEventType, seq);
            if (parentNotFinished) {
                if (!isConcurrent(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                    evType.parentSequenceNumber = seq;
                }
                evType.eventTypeGroupSequenceDependencyIdentity = parentNotFinished.eventTypeGroupSequenceIdentity;
                return evType;
            } else {
                if (!getNotAfterEventTypeId(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                    const sortedEvTypeList = sortOrderedEventTypes(parentEventType.eventTypeGroup?.eventTypeArray);
                    if (sortedEvTypeList && sortedEvTypeList.length > 0) {
                        if (!isConcurrent(evType.eventTypeGroup, node.firstTimeInPortCall)) {
                            evType.parentSequenceNumber = seq;
                        }
                        evType.eventTypeGroupSequenceDependencyIdentity =
                            sortedEvTypeList[sortedEvTypeList.length - 1].eventTypeGroupSequenceIdentity;
                        return evType;
                    }
                }
            }
        }
        return evType;
    };

    const getParentEventTypeList = (
        eventArray: TankersEvent[],
        evType: EventType,
        seq: number | null,
        evTypeArray: EventType[],
        node: IRotationNode
    ): ProcessItem[] => {
        const parentList: ProcessItem[] = [];
        const evCreated = Boolean(
            eventArray.find((ev) => ev.eventType?.eventTypeGroupId === evType.eventTypeGroupId && (!seq || ev.concurrentSequenceNumber === seq))
        );
        const allSeqFinished = !isSeqNotFinished(eventArray, evType, seq);

        const newSeq = getNextSeqNr(eventArray, evType, seq, node);

        const newEvType = createNewEventType(evType);
        newEvType.displayNameToolbox = evType.displayNameToolbox;
        const updatedEv = updateEventType(eventArray, newEvType, newSeq, node);
        parentList.push(new ProcessItem(updatedEv, updatedEv.parentSequenceNumber, evCreated, allSeqFinished));
        if (getRequiredPreviousTypeId(newEvType.eventTypeGroup, node.firstTimeInPortCall)) {
            // Find ancestor
            let keepGoing = true;
            let prevEvType = newEvType;
            while (keepGoing) {
                const reqPrevTypeId = getRequiredPreviousTypeId(prevEvType.eventTypeGroup, node.firstTimeInPortCall);
                if (reqPrevTypeId) {
                    let parentEvType: EventType | null = evTypeArray.find((et) => et.eventTypeId === reqPrevTypeId) ?? null;
                    if (parentEvType) {
                        const allSeqFinished = !isSeqNotFinished(eventArray, parentEvType, seq);
                        if (!getNotAfterEventTypeId(prevEvType.eventTypeGroup, node.firstTimeInPortCall)) {
                            const sortedEvTypeList = sortOrderedEventTypes(parentEvType.eventTypeGroup?.eventTypeArray);
                            if (sortedEvTypeList && sortedEvTypeList.length > 0) {
                                parentEvType = sortedEvTypeList[0];
                            }
                        }
                        const newParentEvType = createNewEventType(parentEvType);
                        parentList.unshift(new ProcessItem(updateEventType(eventArray, newParentEvType, newSeq, node), seq, true, allSeqFinished));
                        prevEvType = parentEvType;
                    } else {
                        keepGoing = false;
                    }
                } else {
                    keepGoing = false;
                }
            }
        }
        return parentList;
    };

    const isSeqNotFinished = (eventArray: TankersEvent[], evType: EventType, seq: number | null): boolean => {
        const eventListForGroup = filterSameEventTypeGroupAndLevel(eventArray, evType);
        if (eventListForGroup && eventListForGroup.length > 0) {
            let seqArray: TankersEvent[] = [];
            if (seq) {
                seqArray = eventListForGroup.filter((ev) => ev.concurrentSequenceNumber === seq);
            } else {
                seqArray = eventListForGroup;
            }
            if (seqArray && seqArray.length > 0) {
                const ind = findMissingEventTypeInSequence(seqArray);
                if (ind !== -1) {
                    return true;
                }
            }
        }
        return false;
    };

    const evTypeNotFinished = (eventArray: TankersEvent[], evType: EventType, seq: number | null): TankersEvent | null => {
        const eventListForGroup = eventArray.filter((ev) => ev.eventType?.eventTypeGroupId === evType.eventTypeGroupId);
        if (eventListForGroup && eventListForGroup.length > 0) {
            let seqArray: TankersEvent[] = [];
            if (seq) {
                seqArray = eventListForGroup.filter((ev) => ev.concurrentSequenceNumber === seq);
            } else {
                seqArray = eventListForGroup;
            }
            if (seqArray && seqArray.length > 0) {
                const ind = findMissingEventTypeInSequence(seqArray);
                if (ind !== -1) {
                    return seqArray[ind];
                }
            }
        }
        return null;
    };

    const createProcessInstanceDisplayName = (evType: EventType, seq: number, concurrentDisplayStr: string | null): string => {
        if (concurrentDisplayStr) {
            return evType.eventTypeGroup?.name + ' ' + concurrentDisplayStr;
        }
        if (evType.dependencyDisplayName) {
            return evType.eventTypeGroup?.name + ' ' + evType.dependencyDisplayName;
        }
        return evType.eventTypeGroup?.name + ' (' + seq.toString() + ')';
    };

    const createProcessInstance = (
        parentEventType: ProcessItem,
        prcList: EventTypeProcess[],
        prc: EventTypeProcess,
        lastElem: boolean,
        sequenceNr: number | null,
        concurrentDisplayStr: string | null,
        node: IRotationNode
    ): EventTypeProcess[] => {
        // If event sequence started and event type is concurrent
        if (sequenceNr && !parentEventType.allSeqFinished && isConcurrent(parentEventType?.eventType?.eventTypeGroup, node.firstTimeInPortCall)) {
            const evTypePrcItem: EventTypeProcessInstance | null =
                prc?.evTypePrcInstanceList.find((etpi) => etpi.sequenceNr === parentEventType.sequenceNr) ?? null;
            if (!evTypePrcItem) {
                const newPrcList: EventTypeProcess[] = [];
                // Conditionally create EventTypeProcess
                if (lastElem) {
                    newPrcList.push(
                        new EventTypeProcess(
                            parentEventType.eventType.eventTypeGroup,
                            parentEventType.eventType,
                            sequenceNr,
                            isConcurrent(parentEventType.eventType.eventTypeGroup, node.firstTimeInPortCall),
                            parentEventType.eventType.actualMandatory,
                            [],
                            [],
                            false,
                            true,
                            parentEventType.allSeqFinished,
                            false,
                            false,
                            ''
                        )
                    );
                }
                //const instName = parentEventType!.eventType!.eventTypeGroup!.name + ' (' + parentEventType.sequenceNr!.toString() + ')';
                const instName = createProcessInstanceDisplayName(parentEventType.eventType, parentEventType.sequenceNr, concurrentDisplayStr);
                const prcInst = new EventTypeProcessInstance(parentEventType.sequenceNr, instName, newPrcList, false, false);
                prc.evTypePrcInstanceList.push(prcInst);
                prcList = prcInst.evTypePrcList;
            } else {
                prcList = evTypePrcItem.evTypePrcList;
            }
        }
        return prcList;
    };

    const createPrc = (
        evType: EventType,
        evCreated: boolean,
        allSeqFinished: boolean,
        lastElem: boolean,
        seq: number | null,
        createFromInst: boolean,
        node: IRotationNode
    ): EventTypeProcess[] => {
        const prcList: EventTypeProcess[] = [];

        if (evCreated && lastElem && !allSeqFinished && createFromInst) {
            const prc = new EventTypeProcess(
                evType.eventTypeGroup,
                evType,
                seq,
                isConcurrent(evType.eventTypeGroup, node.firstTimeInPortCall),
                evType.actualMandatory,
                [],
                [],
                false,
                true,
                allSeqFinished,
                false,
                false,
                ''
            );
            prcList.push(prc);
        }
        return prcList;
    };

    const getCorrectEvType = (evType: EventType, node: IRotationNode): EventType => {
        if (isConcurrent(evType.eventTypeGroup, node.firstTimeInPortCall)) {
            const sortedEvTypeList = sortOrderedEventTypes(evType.eventTypeGroup?.eventTypeArray);
            //sortedEvTypeList[0].parentSequenceNumber = null;
            //return sortedEvTypeList[0];
            if (sortedEvTypeList[0].eventTypeId !== evType.eventTypeId) {
                sortedEvTypeList[0].parentSequenceNumber = null;
                return sortedEvTypeList[0];
            } else {
                evType.parentSequenceNumber = null;
            }
        }
        return evType;
    };

    const sortAllProcesses = (evTypePrcList: EventTypeProcess[]) => {
        if (evTypePrcList && evTypePrcList.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (evTypePrcList[ind].evTypePrcList && evTypePrcList[ind].evTypePrcList.length > 0) {
                    sortAllProcesses(evTypePrcList[ind].evTypePrcList);
                } else if (evTypePrcList[ind].evTypePrcInstanceList && evTypePrcList[ind].evTypePrcInstanceList.length > 0) {
                    let keepGoingInst = true;
                    let indInst = 0;
                    while (keepGoingInst) {
                        if (
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList &&
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList.length > 0
                        ) {
                            sortAllProcesses(evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList);
                        }
                        indInst++;
                        if (indInst === evTypePrcList[ind].evTypePrcInstanceList.length) {
                            keepGoingInst = false;
                        }
                    }
                    evTypePrcList[ind].evTypePrcInstanceList = evTypePrcList[ind].evTypePrcInstanceList.sort((a, b) =>
                        a.sequenceNr > b.sequenceNr ? 1 : -1
                    );
                }
                ind++;
                if (ind === evTypePrcList.length) {
                    keepGoing = false;
                }
            }
            evTypePrcList = setDisplayNameAndSortProcessList(evTypePrcList);
        }
        return;
    };

    const updateIsSelected = (evTypePrcList: EventTypeProcess[], toolboxQueryInfo: ToolboxQueryInfo): boolean => {
        if (evTypePrcList && evTypePrcList.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (evTypePrcList[ind].evTypePrcList && evTypePrcList[ind].evTypePrcList.length > 0) {
                    if (updateIsSelected(evTypePrcList[ind].evTypePrcList, toolboxQueryInfo)) {
                        evTypePrcList[ind].isExpanded = true;
                        return true;
                    }
                } else if (evTypePrcList[ind].evTypePrcInstanceList && evTypePrcList[ind].evTypePrcInstanceList.length > 0) {
                    let keepGoingInst = true;
                    let indInst = 0;
                    while (keepGoingInst) {
                        if (
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList &&
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList.length > 0
                        ) {
                            if (updateIsSelected(evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList, toolboxQueryInfo)) {
                                evTypePrcList[ind].isExpanded = true;
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].isExpanded = true;
                                return true;
                            }
                        }
                        indInst++;
                        if (indInst === evTypePrcList[ind].evTypePrcInstanceList.length) {
                            keepGoingInst = false;
                        }
                    }
                    if (
                        evTypePrcList[ind].eventType?.eventTypeId === +toolboxQueryInfo.eventTypeId &&
                        (!toolboxQueryInfo.seqNr || +toolboxQueryInfo.seqNr === evTypePrcList[ind].sequenceNr)
                    ) {
                        evTypePrcList[ind].isSelected = true;
                        return true;
                    }
                } else {
                    if (
                        evTypePrcList[ind].eventType?.eventTypeId === +toolboxQueryInfo.eventTypeId &&
                        (!toolboxQueryInfo.seqNr || +toolboxQueryInfo.seqNr === evTypePrcList[ind].sequenceNr)
                    ) {
                        evTypePrcList[ind].isSelected = true;
                        return true;
                    }
                }
                ind++;
                if (ind === evTypePrcList.length) {
                    keepGoing = false;
                }
            }
        }
        return false;
    };

    const updateIsSelectable = (eventArray: TankersEvent[], evTypePrc: EventTypeProcess, node: IRotationNode): boolean => {
        const seqNotFinished = isSeqNotFinished(eventArray, evTypePrc.eventType, evTypePrc.sequenceNr);
        let hasChildrenOnlyDependentOnReq = false;

        // Find children dependent on required and notAfterEventTypeId is null
        if (!seqNotFinished) {
            const sortedEvTypeList = sortOrderedEventTypes(evTypePrc.eventTypeGroup?.eventTypeArray);
            if (sortedEvTypeList && sortedEvTypeList.length > 0) {
                const evDep = eventArray.find(
                    (ev) =>
                        ev.concurrentSequenceNumber === evTypePrc.sequenceNr &&
                        ev.eventType.eventTypeGroup.requiredPreviousTypeId === sortedEvTypeList[sortedEvTypeList.length - 1].eventTypeId &&
                        !getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall)
                );
                if (evDep) hasChildrenOnlyDependentOnReq = true;
            }
        }

        return (
            evTypePrc.isConcurrent ||
            !evTypePrc.evTypePrcList ||
            evTypePrc.evTypePrcList.length === 0 ||
            (isMultiple(evTypePrc.eventType?.eventTypeGroup, node.firstTimeInPortCall) && !seqNotFinished && !hasChildrenOnlyDependentOnReq)
        );
    };

    const updateIsExpandable = (evTypePrc: EventTypeProcess): boolean => {
        return (
            (evTypePrc.evTypePrcInstanceList && evTypePrc.evTypePrcInstanceList.length > 0) ||
            (evTypePrc.evTypePrcList && evTypePrc.evTypePrcList.length > 0)
        );
    };

    const updateProperties = (eventArray: TankersEvent[], evTypePrcList: EventTypeProcess[], node: IRotationNode) => {
        evTypePrcList.forEach((evTypePrcItem) => {
            evTypePrcItem.evTypePrcInstanceList.forEach((prcInstItem) => {
                updateProperties(eventArray, prcInstItem.evTypePrcList, node);
            });
            evTypePrcItem.isExpandable = updateIsExpandable(evTypePrcItem);
            evTypePrcItem.isSelectable = updateIsSelectable(eventArray, evTypePrcItem, node);
            updateProperties(eventArray, evTypePrcItem.evTypePrcList, node);
        });
    };

    const isProcessMandatory = (
        evType: EventType,
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        node: IRotationNode,
        seqNumber: number | null
    ): boolean => {
        let sequenceNumber: number | null = evType.parentSequenceNumber;
        if (seqNumber) sequenceNumber = seqNumber;
        const evListSameSequence: TankersEvent[] = eventArray.filter((event) => {
            if (!sequenceNumber) return event.eventType && event.eventType.eventTypeGroupId === evType.eventTypeGroup?.eventTypeGroupId;
            return (
                event.eventType &&
                event.eventType.eventTypeGroupId === evType.eventTypeGroup?.eventTypeGroupId &&
                event.concurrentSequenceNumber === sequenceNumber
            );
        });
        const evListSameGroup: TankersEvent[] = eventArray.filter((event) => {
            return event.eventType && event.eventType.eventTypeGroupId === evType.eventTypeGroup?.eventTypeGroupId;
        });
        if (evListSameSequence && evListSameSequence.length > 0) {
            const indEv = findMissingEventTypeInSequence(evListSameSequence);
            if (indEv !== -1) {
                // Not finished sequence
                return true;
            }
        } else if (evListSameGroup && evListSameGroup.length > 0) {
            // return evType.mandatory && isMandatory(evType.eventTypeGroup , node.firstTimeInPortCall);
            if (hasConcurrentParentInCompleteTree(evType, eventTypeData, node)) {
                return evType.mandatory && isMandatory(evType.eventTypeGroup, node.firstTimeInPortCall);
            }
            if (sequenceNumber) {
                const evList2 = evListSameGroup.find((ev) => ev.concurrentSequenceNumber !== seqNumber);
                if (evList2) return false;
            } else {
                return evType.mandatory && isMandatory(evType.eventTypeGroup, node.firstTimeInPortCall);
            }
        } else {
            return evType.mandatory && isMandatory(evType.eventTypeGroup, node.firstTimeInPortCall);
        }

        // let isActMand = evType.mandatory && evType.eventTypeGroup!.mandatory;
        //let isActMand = evType.actualMandatory;
        let isActMand = false;
        const sortedEvTypeList = sortOrderedEventTypes(evType.eventTypeGroup?.eventTypeArray);
        const lastEventTypeInGroup = sortedEvTypeList[sortedEvTypeList.length - 1];

        const grArray = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
        const etGrList = grArray.filter((etg) => {
            if (!getNotAfterEventTypeId(etg, node.firstTimeInPortCall)) {
                return (
                    getRequiredPreviousTypeId(etg, node.firstTimeInPortCall) === lastEventTypeInGroup.eventTypeId &&
                    etg.eventTypeLevel?.eventTypeLevelId === node.type
                );
            }
            return (
                getRequiredPreviousTypeId(etg, node.firstTimeInPortCall) === evType.eventTypeId && etg.eventTypeLevel?.eventTypeLevelId === node.type
            );
        });
        if (etGrList && etGrList.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                const eventListForGroup: TankersEvent[] = eventArray.filter((event) => {
                    if (!sequenceNumber) return event.eventType && event.eventType.eventTypeGroupId === etGrList[ind].eventTypeGroupId;
                    return (
                        event.eventType &&
                        event.eventType.eventTypeGroupId === etGrList[ind].eventTypeGroupId &&
                        event.concurrentSequenceNumber === sequenceNumber
                    );
                });
                if (!eventListForGroup || eventListForGroup.length === 0) {
                    isActMand = isMandatory(etGrList[ind], node.firstTimeInPortCall) && etGrList[ind].eventTypeArray[0].mandatory;
                    keepGoing = !isActMand;
                } else {
                    const indSeq = findMissingEventTypeInSequence(eventListForGroup);
                    if (indSeq !== -1) {
                        isActMand = eventListForGroup[indSeq].eventType?.mandatory;
                        keepGoing = !isActMand;
                    }
                }
                if (keepGoing) {
                    ind++;
                    keepGoing = ind < etGrList.length;
                }
            }
        }
        return isActMand;
    };

    const createProcessList = (
        eventArray: TankersEvent[],
        toolboxEvTypeArray: EventType[],
        evTypeArray: EventType[],
        evTypePrcList: EventTypeProcess[],
        eventTypeData: EventTypeData,
        selectedNode: IRotationNode
    ): EventTypeProcess[] => {
        toolboxEvTypeArray.forEach((evType) => {
            // Get dependent parent event type list
            const parentEvTypeListOfList = getParentEventTypeListOfList(eventArray, evType, evTypeArray, eventTypeData, selectedNode);
            if (parentEvTypeListOfList && parentEvTypeListOfList.length > 0) {
                parentEvTypeListOfList.forEach((parentEvTypeList) => {
                    let currentPrcList: EventTypeProcess[] = evTypePrcList;
                    let count = 1;
                    parentEvTypeList.parentList.forEach((parentEventType) => {
                        // Has the event type process already been created, if not: create the process
                        let evTypePrc: EventTypeProcess | null =
                            currentPrcList.find(
                                (etp) => etp.eventTypeGroup.eventTypeGroupId === parentEventType.eventType.eventTypeGroup?.eventTypeGroupId
                            ) ?? null;
                        if (!evTypePrc) {
                            evTypePrc = new EventTypeProcess(
                                parentEventType.eventType.eventTypeGroup,
                                getCorrectEvType(parentEventType.eventType, selectedNode),
                                parentEventType.sequenceNr,
                                isConcurrent(parentEventType.eventType.eventTypeGroup, selectedNode.firstTimeInPortCall),
                                isProcessMandatory(parentEventType.eventType, eventArray, eventTypeData, selectedNode, parentEventType.sequenceNr),
                                [],
                                createPrc(
                                    parentEventType.eventType,
                                    parentEventType.eventCreated,
                                    parentEventType.allSeqFinished,
                                    count === parentEvTypeList.parentList.length,
                                    parentEventType.sequenceNr,
                                    !isConcurrent(parentEventType.eventType.eventTypeGroup, selectedNode.firstTimeInPortCall),
                                    selectedNode
                                ),
                                false,
                                false,
                                parentEventType.allSeqFinished,
                                false,
                                false,
                                ''
                            );
                            currentPrcList.push(evTypePrc);
                            if (isSeqNotFinished(eventArray, parentEventType.eventType, parentEventType.sequenceNr)) {
                                const concurrentDisplayStr: string | null = null;

                                currentPrcList = createProcessInstance(
                                    parentEventType,
                                    evTypePrc?.evTypePrcList,
                                    evTypePrc,
                                    count === parentEvTypeList.parentList.length,
                                    parentEventType.sequenceNr,
                                    concurrentDisplayStr,
                                    selectedNode
                                );
                                if (evTypePrc.isConcurrent) {
                                    evTypePrc.sequenceNr = null;
                                }
                            } else {
                                currentPrcList = evTypePrc?.evTypePrcList;
                            }
                        } else {
                            if (evTypePrc.evTypePrcInstanceList && evTypePrc.evTypePrcInstanceList.length > 0) {
                                const inst = evTypePrc.evTypePrcInstanceList.find((etpi) => etpi.sequenceNr === parentEventType.sequenceNr);
                                if (inst) {
                                    currentPrcList = inst.evTypePrcList;
                                } else if (evType.actualMandatory) {
                                    if (isSeqNotFinished(eventArray, parentEventType.eventType, parentEventType.sequenceNr)) {
                                        const concurrentDisplayStr: string | null = null;
                                        currentPrcList = createProcessInstance(
                                            parentEventType,
                                            evTypePrc?.evTypePrcList,
                                            evTypePrc,
                                            count === parentEvTypeList.parentList.length,
                                            parentEventType.sequenceNr,
                                            concurrentDisplayStr,
                                            selectedNode
                                        );
                                        if (evTypePrc.isConcurrent) {
                                            evTypePrc.sequenceNr = null;
                                        }
                                    } else {
                                        currentPrcList = evTypePrc?.evTypePrcList;
                                    }
                                }
                            } else {
                                currentPrcList = evTypePrc?.evTypePrcList;
                            }
                        }
                        count++;
                    });
                });
            } else {
                const prcExists = evTypePrcList.find((p) => p.eventTypeGroup.eventTypeGroupId === evType.eventTypeGroupId);
                if (!prcExists) {
                    let allSeqFinished = false;
                    const eventListForGroup = eventArray.filter((ev) => ev.eventType?.eventTypeGroupId === evType?.eventTypeGroupId);
                    const evCreated: boolean = eventListForGroup && eventListForGroup.length > 0;
                    if (evCreated) {
                        allSeqFinished = !isSeqNotFinished(eventListForGroup, evType, null);
                    }
                    let seqNr: number | null = null;
                    if (!isConcurrent(evType.eventTypeGroup, selectedNode.firstTimeInPortCall)) seqNr = 1;
                    const prc = new EventTypeProcess(
                        evType.eventTypeGroup,
                        getCorrectEvType(evType, selectedNode),
                        seqNr,
                        isConcurrent(evType.eventTypeGroup, selectedNode.firstTimeInPortCall),
                        isProcessMandatory(evType, eventArray, eventTypeData, selectedNode, seqNr),
                        [],
                        createPrc(
                            evType,
                            eventListForGroup.length > 0,
                            allSeqFinished,
                            true,
                            null,
                            !isConcurrent(evType.eventTypeGroup, selectedNode.firstTimeInPortCall),
                            selectedNode
                        ),
                        false,
                        false,
                        allSeqFinished,
                        false,
                        false,
                        ''
                    );
                    evTypePrcList.push(prc);
                    if (evCreated && !allSeqFinished && isConcurrent(evType.eventTypeGroup, selectedNode.firstTimeInPortCall)) {
                        //const instName = evType.eventTypeGroup!.name + ' (' + seqNr.toString() + ')';
                        const instName = evType.eventTypeGroup?.name + ' (1)';
                        const prcInst = new EventTypeProcessInstance(
                            1,
                            instName,
                            [], //createPrc(evType, true, allSeqFinished, false, null, true),
                            false,
                            false
                        );
                        prc.evTypePrcInstanceList.push(prcInst);
                    }
                }
            }
        });
        updateProperties(eventArray, evTypePrcList, selectedNode);
        return evTypePrcList;
    };

    // Main method for populating event type process list in toolbox
    const getEventTypeProcesses = (
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        selectedNode: IRotationNode,
        isRetroactiveCargoModeEnabled = false
    ): EventTypeProcessOverview => {
        const availableEventTypeArray = getAvailableEventTypes(eventArray, eventTypeData, selectedNode, isRetroactiveCargoModeEnabled);
        let evTypePrcList: EventTypeProcess[] = [];
        if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            const level = eventTypeData.eventTypeLevelArray.find((lv) => lv.eventTypeLevelId === selectedNode.type);
            if (level) {
                const evTypeArray = level.eventTypeGroupArray.flatMap((o) => o.eventTypeArray);
                const obj = getRequiredContextObj(selectedNode, selectedNode.type);
                if (obj) {
                    const contextSpecificEventList = filterOnContext(obj, eventArray);
                    const mandatoryList = getMandatoryOptionalList(
                        availableEventTypeArray,
                        true,
                        selectedNode,
                        contextSpecificEventList,
                        eventTypeData
                    );
                    const optionalList = getMandatoryOptionalList(
                        availableEventTypeArray,
                        false,
                        selectedNode,
                        contextSpecificEventList,
                        eventTypeData
                    );

                    evTypePrcList = createProcessList(
                        contextSpecificEventList,
                        mandatoryList,
                        evTypeArray,
                        evTypePrcList,
                        eventTypeData,
                        selectedNode
                    );
                    evTypePrcList = createProcessList(
                        contextSpecificEventList,
                        optionalList,
                        evTypeArray,
                        evTypePrcList,
                        eventTypeData,
                        selectedNode
                    );
                }
            }
        }
        return new EventTypeProcessOverview(evTypePrcList);
    };

    return {
        getEventTypeProcesses,
        setDisplayNameAndSortProcessList,
        sortAllProcesses,
        getNextSeqNr,
        updateIsSelected,
    };
};
